import React, { useMemo, useState } from "react";
import { Box, Typography, Button, Tooltip, Stack } from "@mui/material";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import { CANADA_PROVINCES, VIEWBOX } from "../../../data/canadaProvinces";
import { resolveSiteGeo } from "../../../data/siteGeo";
import SitesMapModal from "./SitesMapModal";

// ── Theme constants (mirror SitesMapModal / DashboardBanner navy-cyan) ──
const ACCENT = "#7dd3fc";
const MAP_BG_TOP = "#13243f";
const MAP_BG_BOTTOM = "#0a1322";

const STATUS_META = {
  online: { label: "Online", color: "#34d399" },
  maintenance: { label: "Maintenance", color: "#fbbf24" },
  unknown: { label: "Unknown", color: "#94a3b8" },
  offline: { label: "Offline", color: "#64748b" },
};

// Province → marker anchor (area-weighted centroid), in VIEWBOX coordinates.
// Kept in sync with SitesMapModal's PROVINCE_CENTERS.
const PROVINCE_CENTERS = {
  ab: { x: 237.3, y: 842.93 },
  bc: { x: 145.23, y: 844.95 },
  mb: { x: 390.99, y: 846.81 },
  nb: { x: 669.08, y: 969.23 },
  nl: { x: 708.56, y: 859.92 },
  nt: { x: 187.17, y: 674.7 },
  ns: { x: 691.14, y: 991.4 },
  nu: { x: 569.68, y: 151.06 },
  on: { x: 490.93, y: 913.45 },
  pe: { x: 697.34, y: 972.45 },
  qc: { x: 619.43, y: 860.07 },
  sk: { x: 314.86, y: 853.77 },
  yt: { x: 47.91, y: 690.93 },
};

const PROV_BY_ID = CANADA_PROVINCES.reduce((acc, p) => {
  acc[p.id] = p;
  return acc;
}, {});

const provinceCenter = (provId) => {
  if (PROVINCE_CENTERS[provId]) return PROVINCE_CENTERS[provId];
  const prov = PROV_BY_ID[provId];
  return prov ? { x: prov.bbox.x + prov.bbox.w / 2, y: prov.bbox.y + prov.bbox.h / 2 } : { x: 0, y: 0 };
};

const clusterStatus = (members) => {
  if (members.some((m) => m.site.status === "offline")) return "offline";
  if (members.some((m) => m.site.status === "maintenance")) return "maintenance";
  if (members.every((m) => m.site.status === "online")) return "online";
  return "unknown";
};

/**
 * SitesMapPanel — a compact, always-visible federation map that lives inline on
 * the Dashboard (left of Recent Activity). Renders a projected map of Canada
 * with one marker per host province, and a "View full map" button that opens
 * the full interactive SitesMapModal. Surfaces the map that was previously
 * reachable only from the banner's "X of Y sites online" pill.
 */
const SitesMapPanel = ({ sites = [], capabilitiesBySite = {} }) => {
  const [mapOpen, setMapOpen] = useState(false);

  // Resolve each site → province (skip the unlocatable).
  const placed = useMemo(() => {
    const arr = [];
    (sites || []).forEach((site) => {
      const geo = resolveSiteGeo(site);
      const provId = geo ? (geo.province || "").toLowerCase() : null;
      const prov = provId ? PROV_BY_ID[provId] : null;
      if (geo && prov) arr.push({ site, geo, provId });
    });
    return arr;
  }, [sites]);

  const sitesByProv = useMemo(() => {
    const map = {};
    placed.forEach((p) => (map[p.provId] = map[p.provId] || []).push(p));
    return map;
  }, [placed]);

  const provinceMarkers = useMemo(
    () =>
      Object.keys(sitesByProv).map((provId) => {
        const members = sitesByProv[provId];
        const center = provinceCenter(provId);
        return {
          provId,
          x: center.x,
          y: center.y,
          members,
          status: clusterStatus(members),
        };
      }),
    [sitesByProv]
  );

  const hostProvinces = useMemo(() => new Set(placed.map((p) => p.provId)), [placed]);

  const totalCount = (sites || []).length;
  const onlineCount = (sites || []).filter((s) => s?.status === "online").length;
  const allOnline = totalCount > 0 && onlineCount === totalCount;
  const statusColor = totalCount === 0 ? ACCENT : allOnline ? "#86efac" : "#fbbf24";

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        minHeight: 420,
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid var(--fx-border)",
        boxShadow: "var(--fx-shadow-sm)",
        bgcolor: "var(--fx-paper)",
      }}
    >
      {/* Header bar — navy, matching the banner / modal */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          flexShrink: 0,
          background: "linear-gradient(135deg, #1B365D 0%, #132643 60%, #0b1629 100%)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          gap: 1.25,
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1.25,
            bgcolor: "rgba(255,255,255,0.10)",
            border: "1px solid rgba(255,255,255,0.18)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: ACCENT,
            flexShrink: 0,
          }}
        >
          <PublicOutlinedIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: ACCENT, fontSize: "0.5625rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase" }}>
            Federation Map
          </Typography>
          <Typography sx={{ color: "#fff", fontSize: "0.875rem", fontWeight: 700, lineHeight: 1.2 }}>
            {totalCount} site{totalCount === 1 ? "" : "s"} across {hostProvinces.size} province{hostProvinces.size === 1 ? "" : "s"}
          </Typography>
        </Box>
        {/* Live online pill */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.6,
            px: 1,
            py: 0.4,
            borderRadius: 999,
            bgcolor: "rgba(255,255,255,0.08)",
            border: "1px solid rgba(255,255,255,0.15)",
            color: statusColor,
            fontSize: "0.65rem",
            fontWeight: 700,
            fontFamily: "var(--fx-font-mono)",
            fontVariantNumeric: "tabular-nums",
            flexShrink: 0,
          }}
        >
          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: statusColor, boxShadow: `0 0 6px ${statusColor}` }} />
          {onlineCount}/{totalCount}
        </Box>
      </Box>

      {/* Map surface — flexes to fill; click anywhere to open the full map */}
      <Box
        sx={{
          position: "relative",
          flex: 1,
          minHeight: 240,
          background: `radial-gradient(120% 90% at 50% 0%, ${MAP_BG_TOP} 0%, ${MAP_BG_BOTTOM} 100%)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
        role="button"
        tabIndex={0}
        aria-label="Open the full federation map"
        onClick={() => setMapOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setMapOpen(true);
          }
        }}
      >
        <Box
          component="svg"
          viewBox={`0 0 ${VIEWBOX.w} ${VIEWBOX.h}`}
          preserveAspectRatio="xMidYMid meet"
          // Absolute-fill so the (tall) Canada viewBox can't drive the panel's
          // intrinsic height and push it past the Recent Activities column. The
          // panel height is then set purely by the Grid row stretch, keeping the
          // map in line with the title + activity cards on the right.
          sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block", p: 1.5 }}
        >
          <defs>
            <radialGradient id="fx-panel-marker-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={ACCENT} stopOpacity="0.55" />
              <stop offset="100%" stopColor={ACCENT} stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Provinces */}
          {CANADA_PROVINCES.map((p) => {
            const isHost = hostProvinces.has(p.id);
            return (
              <path
                key={p.id}
                d={p.path}
                fill={isHost ? "rgba(0,135,180,0.22)" : "rgba(125,211,252,0.05)"}
                stroke={isHost ? "rgba(125,211,252,0.6)" : "rgba(125,211,252,0.22)"}
                strokeWidth={isHost ? 1.1 : 0.7}
                strokeLinejoin="round"
              />
            );
          })}

          {/* Province markers */}
          {provinceMarkers.map((c) => {
            const status = STATUS_META[c.status] || STATUS_META.unknown;
            const online = c.status === "online";
            const count = c.members.length;
            const r = count > 1 ? 11 : 7;
            return (
              <g key={c.provId} transform={`translate(${c.x}, ${c.y})`}>
                {online && (
                  <circle r={r + 5} fill="url(#fx-panel-marker-glow)">
                    <animate attributeName="opacity" values="0.9;0.4;0.9" dur="2.4s" repeatCount="indefinite" />
                  </circle>
                )}
                <circle r={r} fill={status.color} stroke="#0a1322" strokeWidth="1.8" />
                {count > 1 ? (
                  <text textAnchor="middle" dominantBaseline="central" fontSize="11" fontWeight="700" fill="#0a1322">
                    {count}
                  </text>
                ) : (
                  <circle r="2" fill="#fff" opacity="0.92" />
                )}
              </g>
            );
          })}
        </Box>

        {/* Legend — bottom-left overlay */}
        <Box
          sx={{
            position: "absolute",
            left: 12,
            bottom: 12,
            display: "flex",
            gap: 1,
            px: 1,
            py: 0.6,
            borderRadius: 1.5,
            bgcolor: "rgba(10,19,34,0.6)",
            border: "1px solid rgba(125,211,252,0.18)",
            backdropFilter: "blur(6px)",
            pointerEvents: "none",
          }}
        >
          {[["online", "Online"], ["maintenance", "Maint."], ["offline", "Offline"]].map(([k, label]) => (
            <Stack key={k} direction="row" alignItems="center" spacing={0.5}>
              <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: STATUS_META[k].color }} />
              <Typography sx={{ fontSize: "0.625rem", color: "rgba(255,255,255,0.8)" }}>{label}</Typography>
            </Stack>
          ))}
        </Box>
      </Box>

      {/* Footer — full-width open-full-map button */}
      <Box sx={{ p: 1.5, borderTop: "1px solid var(--fx-border)", flexShrink: 0 }}>
        <Tooltip title="Open the full interactive federation map" placement="top" arrow>
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            startIcon={<OpenInFullIcon sx={{ fontSize: 16 }} />}
            onClick={() => setMapOpen(true)}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            View full map
          </Button>
        </Tooltip>
      </Box>

      <SitesMapModal
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        sites={sites}
        capabilitiesBySite={capabilitiesBySite}
      />
    </Box>
  );
};

export default SitesMapPanel;
